import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import { cn } from "@/platform/lib/cn";
import { useSession } from "@/platform/auth/use-session";
import { createCheckoutSession } from "@/platform/billing/api";
import {
  EINSTEIN_PLANS,
  USP_PLANS,
  formatBrl,
  type PlanCatalogEntry,
} from "@/platform/billing/plans";
import type { PlatformTenantSlug } from "@/platform/lib/database.types";

const TABS: Array<{ slug: PlatformTenantSlug; label: string; accent: string }> = [
  { slug: "einstein", label: "PsiEinstein · Einstein", accent: "255 78% 62%" },
  { slug: "usp", label: "FuvestPro · USP", accent: "222 65% 50%" },
];

export function PricingPage() {
  const [params, setParams] = useSearchParams();
  const initialTenant = (params.get("tenant") as PlatformTenantSlug | null) ?? "einstein";
  const [activeTenant, setActiveTenant] = useState<PlatformTenantSlug>(
    initialTenant === "usp" ? "usp" : "einstein",
  );
  const plans = activeTenant === "einstein" ? EINSTEIN_PLANS : USP_PLANS;
  const tabConfig = TABS.find((t) => t.slug === activeTenant)!;

  const tenantStyle = {
    "--color-accent": `hsl(${tabConfig.accent})`,
  } as React.CSSProperties;

  function selectTenant(slug: PlatformTenantSlug) {
    setActiveTenant(slug);
    const next = new URLSearchParams(params);
    next.set("tenant", slug);
    setParams(next, { replace: true });
  }

  return (
    <div style={tenantStyle}>
      <section className="relative overflow-hidden bg-mesh">
        <Blob color="violet" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-25 blur-2xl animate-blob" />
        <Blob color="sky" className="pointer-events-none absolute -right-24 top-40 w-80 opacity-25 blur-2xl animate-blob" />
        <SoftStar color="yellow" className="pointer-events-none absolute right-10 bottom-20 w-24 opacity-50 animate-float" />

        <Container size="wide" className="relative pt-20 pb-12 md:pt-28">
          <div className="max-w-3xl">
            <Badge variant="accentSolid" className="gap-2">
              <Sparkles className="size-3" aria-hidden />
              Planos · 7 dias grátis em todos
            </Badge>
            <h1 className="mt-7 font-display text-[length:var(--text-display-xl)] font-semibold leading-[var(--text-display-xl--line-height)] tracking-[var(--text-display-xl--letter-spacing)] text-balance">
              Escolha o produto e o plano que combinam com o seu vestibular.
            </h1>
            <p className="mt-6 max-w-2xl text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
              7 dias grátis para testar. Cancele quando quiser, sem multa. Cobrança mensal,
              em reais, via cartão. Saídas e migrações entre planos sem fricção.
            </p>
          </div>

          {/* Tabs Einstein / USP */}
          <nav
            aria-label="Selecionar produto"
            className="mt-10 inline-flex flex-wrap gap-1 rounded-full border border-[--color-border] bg-[color:var(--color-card)] p-1 shadow-[var(--shadow-sm)]"
          >
            {TABS.map((tab) => {
              const isActive = activeTenant === tab.slug;
              return (
                <button
                  key={tab.slug}
                  type="button"
                  onClick={() => selectTenant(tab.slug)}
                  className={cn(
                    "relative rounded-full px-5 py-2 text-[length:var(--text-body-sm)] font-medium transition-colors duration-150",
                    isActive
                      ? "text-white"
                      : "text-[color:var(--color-muted-foreground)] hover:text-[color:var(--color-foreground)]",
                  )}
                  style={isActive ? { backgroundColor: `hsl(${tab.accent})` } : undefined}
                  aria-pressed={isActive}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </Container>
      </section>

      <section className="py-20 md:py-24">
        <Container size="wide">
          <div className="grid gap-6 pt-4 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => (
              <PlanCard key={plan.slug} plan={plan} />
            ))}
          </div>

          <p className="mt-14 text-center text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] max-w-2xl mx-auto text-pretty">
            Todos os planos incluem 7 dias grátis. Sem fidelidade, cancele a qualquer momento.
            Dúvidas?{" "}
            <Link to="/contato" className="link font-semibold whitespace-nowrap">
              Fale com a gente
            </Link>
            .
          </p>
        </Container>
      </section>
    </div>
  );
}

function PlanCard({ plan }: { plan: PlanCatalogEntry }) {
  const { isAuthenticated } = useSession();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const isComingSoon = plan.status === "coming_soon";

  async function handleClick() {
    if (isComingSoon) {
      navigate(`/usp`);
      return;
    }
    if (!isAuthenticated) {
      navigate(`/cadastro?tenant=${plan.tenant}&plan=${plan.slug}`);
      return;
    }
    setSubmitting(true);
    try {
      const { url } = await createCheckoutSession(plan.tenant, plan.slug);
      window.location.assign(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao iniciar checkout.");
      setSubmitting(false);
    }
  }

  return (
    <article
      className={cn(
        "relative flex flex-col rounded-2xl bg-[color:var(--color-card)] p-7 sm:p-8 shadow-[var(--shadow-sm)] lift",
        // Borda equalizada: featured ganha cor + 2px (não soma com ring).
        plan.featured
          ? "border-2 border-[color:var(--color-accent)]"
          : "border-2 border-[--color-border]",
      )}
    >
      {/* Badge centralizada no topo, sem sobrepor borda. Mantém ritmo entre os 3 cards. */}
      {plan.badge && (
        <span
          className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full px-3 py-1 text-[length:var(--text-caption)] font-semibold uppercase tracking-[0.04em] text-white whitespace-nowrap shadow-[var(--shadow-sm)]"
          style={{ backgroundColor: "var(--color-accent)" }}
        >
          {plan.badge}
        </span>
      )}
      {isComingSoon && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full border border-[--color-border] bg-[color:var(--color-card)] px-3 py-1 text-[length:var(--text-caption)] font-medium uppercase tracking-[0.04em] text-[color:var(--color-muted-foreground)] whitespace-nowrap shadow-[var(--shadow-sm)]">
          Em breve
        </span>
      )}

      <header>
        <h2 className="font-display text-[length:var(--text-display-sm)] font-semibold leading-tight">
          {plan.name}
        </h2>
        <div className="mt-4 flex items-baseline gap-1.5">
          <span
            data-tabular="true"
            className="font-display text-[length:var(--text-display-md)] font-semibold leading-none tabular-nums"
          >
            {formatBrl(plan.priceMonthlyBrl)}
          </span>
          <span className="text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
            /mês
          </span>
        </div>
        <p className="mt-2 text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
          {plan.trialDays} dias grátis
        </p>
      </header>

      <p className="mt-5 min-h-[3.5rem] text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] leading-relaxed">
        {plan.description}
      </p>

      <ul className="mt-6 space-y-3 text-[length:var(--text-body-sm)] flex-1">
        {plan.features.map((feature) => (
          <li key={feature} className="flex gap-2.5">
            <span
              aria-hidden
              className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-full bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent)]"
            >
              <Check className="size-2.5" strokeWidth={3} />
            </span>
            <span className="text-[color:var(--color-foreground)]">{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        size="lg"
        variant={plan.featured ? "gradient" : "primary"}
        className="mt-8 w-full"
        onClick={handleClick}
        disabled={submitting}
      >
        {submitting ? (
          <>
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Abrindo checkout…
          </>
        ) : isComingSoon ? (
          <>
            Entrar na lista de espera
            <ArrowUpRight className="size-4" aria-hidden />
          </>
        ) : isAuthenticated ? (
          <>
            Começar agora
            <ArrowUpRight className="size-4" aria-hidden />
          </>
        ) : (
          <>
            Começar grátis
            <ArrowUpRight className="size-4" aria-hidden />
          </>
        )}
      </Button>
    </article>
  );
}
